// Load the server's client list and allow the user to select one, passing all the details to the invoice form.
import React, { useCallback, useMemo } from "react";

import { Typography, Form, Select, Input } from "antd";
import { FormInstance } from "antd/lib/form";

import {
  Client,
  ClientOption,
} from "../../../../../bitmaker/schemas/Billing/Client";

import { formatClientInformation } from "../../../../../utils";

const { useWatch } = Form;

type ClientLabelData = {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  corporateName?: string | null;
};

const createClientLabel = ({
  firstName,
  lastName,
  companyName,
  corporateName,
}: ClientLabelData): React.ReactNode => {
  const personalName = [firstName, lastName].filter(Boolean).join(" ");
  const hasPersonalName = personalName.trim().length > 0;

  let primaryName = "";
  let secondaryLabelNode: React.ReactNode | null = null;

  if (companyName) {
    primaryName = companyName;
    if (corporateName && corporateName !== companyName) {
      primaryName += ` (${corporateName})`;
    }
    if (hasPersonalName) {
      secondaryLabelNode = (
        <div className="text-xs text-gray-600">{personalName}</div>
      );
    }
  } else if (corporateName) {
    primaryName = corporateName;
    if (hasPersonalName) {
      secondaryLabelNode = (
        <div className="text-xs text-gray-600">{personalName}</div>
      );
    }
  } else if (hasPersonalName) {
    primaryName = personalName;
  }

  return (
    <div>
      {primaryName}
      {secondaryLabelNode}
    </div>
  );
};

const formatClientOptions = (clients: Client[]): ClientOption[] => {
  return clients.map((client) => {
    const labelNode = createClientLabel({
      firstName: client.firstName,
      lastName: client.lastName,
      companyName: client.companyName,
      corporateName: client.corporateName,
    });

    const personalName = [client.firstName, client.lastName]
      .filter(Boolean)
      .join(" ");
    const searchTerms = [
      client.companyName,
      client.corporateName,
      personalName,
    ];
    const searchableLabel = searchTerms
      .filter((term) => term && term.trim().length > 0)
      .join(" ");

    return {
      value: client.id,
      label: labelNode,
      searchLabel: searchableLabel,
      clientData: client,
    } as ClientOption;
  });
};

interface InvoiceClientSelectorProps {
  form: FormInstance;
  clients: Client[];
  loadingClients: boolean;
  fetchClientsError: string | null;
}

export const InvoiceClientSelector: React.FC<InvoiceClientSelectorProps> = ({
  form,
  clients,
  loadingClients,
  fetchClientsError,
}) => {
  const language = useWatch("language", form);

  const billedToLabel = language === "es" ? "Facturado a" : "Billed To";

  const currentClientId = useWatch("clientId", form);
  const clientFirstName = useWatch("clientFirstName", form);
  const clientLastName = useWatch("clientLastName", form);
  const clientCompanyName = useWatch("clientCompanyName", form);
  const clientCorporateName = useWatch("clientCorporateName", form);
  const clientAddressLine = useWatch("clientAddressLine", form);
  const clientTownCity = useWatch("clientTownCity", form);
  const clientStateProvince = useWatch("clientStateProvince", form);
  const clientPostalCode = useWatch("clientPostalCode", form);
  const clientCountry = useWatch("clientCountry", form);
  const clientEmailAddress = useWatch("clientEmailAddress", form);
  const clientPhoneNumber = useWatch("clientPhoneNumber", form);
  const clientTaxName = useWatch("clientTaxName", form);
  const clientTaxNumber = useWatch("clientTaxNumber", form);

  const isEditing = !!currentClientId;
  const noClientsAvailable = clients.length === 0;
  const cannotSelectClient = !!fetchClientsError || noClientsAvailable;

  const clientOptions = useMemo(() => {
    const formattedOptions = formatClientOptions(clients);

    if ((fetchClientsError && isEditing) || (noClientsAvailable && isEditing)) {
      return [
        {
          value: currentClientId,
          label: createClientLabel({
            firstName: clientFirstName,
            lastName: clientLastName,
            companyName: clientCompanyName,
            corporateName: clientCorporateName,
          }),
        } as ClientOption,
      ];
    }

    return formattedOptions;
  }, [
    clients,
    fetchClientsError,
    isEditing,
    noClientsAvailable,
    currentClientId,
    clientFirstName,
    clientLastName,
    clientCompanyName,
    clientCorporateName,
  ]);

  const handleClientChange = useCallback(
    (value: number) => {
      const selectedClient = clientOptions.find(
        (client) => client.value === value,
      );

      if (selectedClient?.clientData) {
        const clientData = selectedClient.clientData;
        form.setFieldsValue({
          clientFirstName: clientData.firstName,
          clientLastName: clientData.lastName,
          clientCompanyName: clientData.companyName,
          clientCorporateName: clientData.corporateName,
          clientEmailAddress: clientData.emailAddress,
          clientPhoneNumber: clientData.phoneNumber,
          clientAddressLine: clientData.addressLine,
          clientTownCity: clientData.townCity,
          clientStateProvince: clientData.stateProvince,
          clientPostalCode: clientData.postalCode,
          clientCountry: clientData.country,
          clientTaxName: clientData.taxName,
          clientTaxNumber: clientData.taxNumber,
        });
      } else {
        form.setFieldsValue({
          clientFirstName: null,
          clientLastName: null,
          clientCompanyName: null,
          clientCorporateName: null,
          clientEmailAddress: null,
          clientPhoneNumber: null,
          clientAddressLine: null,
          clientTownCity: null,
          clientStateProvince: null,
          clientPostalCode: null,
          clientCountry: null,
          clientTaxName: null,
          clientTaxNumber: null,
        });
      }
    },
    [clientOptions, form],
  );

  return (
    <div>
      <div className="font-bold text-green-600 mb-1">{billedToLabel}</div>

      {fetchClientsError && !currentClientId && (
        <Typography.Text type="danger" className="block mb-1 text-sm">
          Clients could not be loaded. You cannot create a new invoice right
          now.
        </Typography.Text>
      )}

      {fetchClientsError && currentClientId && (
        <Typography.Text type="secondary" className="block mb-1 text-sm">
          Clients could not be loaded. Changing the client is temporarily
          disabled.
        </Typography.Text>
      )}

      {!isEditing && !fetchClientsError && noClientsAvailable && (
        <Typography.Text type="danger" className="block mb-1 text-sm">
          No clients available. Please add one in the{" "}
          <a href="/billing/clients" className="underline">
            clients section
          </a>
          .
        </Typography.Text>
      )}

      {isEditing && !fetchClientsError && noClientsAvailable && (
        <Typography.Text type="secondary" className="block mb-1 text-sm">
          No other clients are available. You can keep the current client, but
          changing it is not possible right now.
        </Typography.Text>
      )}

      <Form.Item
        name="clientId"
        className="mb-0"
        rules={
          !isEditing && fetchClientsError
            ? []
            : [{ required: true, message: "Select a client" }]
        }
      >
        <Select
          disabled={cannotSelectClient}
          placeholder="Search or Select Client"
          className="w-full text-left client-select"
          popupClassName="client-select-dropdown"
          placement="bottomRight"
          options={clientOptions}
          onChange={handleClientChange}
          loading={loadingClients}
          showSearch
          optionFilterProp="searchLabel"
          filterOption={(input: string, option: ClientOption | undefined) =>
            (option?.searchLabel ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </Form.Item>
      <div style={{ display: "none" }}>
        <Form.Item name="clientFirstName">
          <Input />
        </Form.Item>
        <Form.Item name="clientLastName">
          <Input />
        </Form.Item>
        <Form.Item name="clientCompanyName">
          <Input />
        </Form.Item>
        <Form.Item name="clientCorporateName">
          <Input />
        </Form.Item>
        <Form.Item name="clientEmailAddress">
          <Input />
        </Form.Item>
        <Form.Item name="clientPhoneNumber">
          <Input />
        </Form.Item>
        <Form.Item name="clientAddressLine">
          <Input />
        </Form.Item>
        <Form.Item name="clientTownCity">
          <Input />
        </Form.Item>
        <Form.Item name="clientStateProvince">
          <Input />
        </Form.Item>
        <Form.Item name="clientPostalCode">
          <Input />
        </Form.Item>
        <Form.Item name="clientCountry">
          <Input />
        </Form.Item>
        <Form.Item name="clientTaxName">
          <Input />
        </Form.Item>
        <Form.Item name="clientTaxNumber">
          <Input />
        </Form.Item>
      </div>

      {currentClientId && (
        <div className="mt-2 text-sm">
          {formatClientInformation({
            clientFirstName,
            clientLastName,
            clientCompanyName,
            clientCorporateName,
            clientAddressLine,
            clientTownCity,
            clientStateProvince,
            clientPostalCode,
            clientCountry,
            clientTaxName,
            clientTaxNumber,
            clientEmailAddress,
            clientPhoneNumber,
          }).map((line, index) => (
            <p key={index} className={index === 0 ? "font-semibold" : ""}>
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
